import { datadogRum } from '@datadog/browser-rum';
import { StatsigClient } from '@statsig/js-client';
import { StatsigProvider as BaseStatsigProvider, StatsigOptions } from '@statsig/react-bindings';
import { secondsToMilliseconds } from 'date-fns';
import { createContext } from 'react';
import { useAsync } from 'react-use';

import { getEnvFromHostname } from '@growtherapy/analytics';

import { ChromeToolbarPlaybackControlsSingleton } from '../classes/ChromeToolbarPlaybackControlsSingleton';
import { LoadingScreen } from '../components/LoadingScreen';
import { logger } from '../datadog/logger';
import { getCurrentHost, withRetry } from '../utils';
import {
  BLOCK_STATSIG_REQUESTS_KEY,
  STATSIG_STALE_CACHE_CLEARED_AT_KEY,
  TARGETED_APP_STATSIG_CLIENT_KEY,
} from './constants';
import { subscribeStatsigExposuresToDatadog } from './statsigDatadogExposure';
import { getStatsigProxyConfig } from './statsigProxyConfig';
import { clearStaleStatsigCache } from './utils';

// NOTE: These constants are copied here from src/config.ts to avoid an import cycle.
const isDevMode = import.meta.env.DEV;
const IS_DEV_APP_SETTINGS_ENABLED_KEY = 'is-dev-app-settings-enabled';

export const StatsigLoadingContext = createContext<boolean>(false);

type StatsigProviderOptions = StatsigOptions & {
  gateEvaluationCallback?: (key: string, value: boolean) => void;
  api?: string;
};

function isStatsigBlockedByDevtools(): boolean {
  // Ensure this code is only executed when running in development mode
  // (locally) and when the dev app settings are enabled.
  if (!isDevMode) return false;
  if (localStorage.getItem(IS_DEV_APP_SETTINGS_ENABLED_KEY) !== '1') return false;
  try {
    return localStorage.getItem(BLOCK_STATSIG_REQUESTS_KEY) === 'true';
  } catch {
    return false;
  }
}

const environment = getEnvFromHostname({ hostname: getCurrentHost() });
const statsigOptions: StatsigProviderOptions = {
  environment: {
    tier: environment,
  },
  // https://docs.datadoghq.com/real_user_monitoring/guide/setup-feature-flag-data-collection/?tab=browser#statsig-integration
  gateEvaluationCallback(key: string, value: boolean) {
    datadogRum.addFeatureFlagEvaluation(key, value);
  },
  networkConfig: {
    ...getStatsigProxyConfig(environment),
    preventAllNetworkTraffic: isStatsigBlockedByDevtools(),
  },
};

/**
 * Clears old, global Statsig cache entries that are no longer relevant to the
 * targeted app, if they have not already been cleared. This is to support a
 * migration from the old, global Statsig client to the new, targeted app
 * Statsig client.
 */
function handleClearingStaleStatsigCacheForTargetedAppMigration() {
  const lastClearedStaleCacheDate = localStorage.getItem(STATSIG_STALE_CACHE_CLEARED_AT_KEY);

  if (lastClearedStaleCacheDate) return; // already cleared once for the migration

  const [result, error] = clearStaleStatsigCache(new Date());

  // A 'noop' result means there was no legacy cache index to act on, so there
  // is nothing to report. Skipping it keeps this import-time log out of every
  // page load (and out of every test that spies on `logger.info`).
  //
  // We can remove this entire function once this log statement produces
  // entirely 0 result values for a while
  if (result !== 'noop') {
    logger.info('Cleared stale Statsig cache for targeted app migration', {
      result,
    });
  }

  if (error) {
    logger.error(
      'Failed to clear stale Statsig cache for targeted app migration',
      {
        lastClearedStaleCacheDate,
      },
      error,
    );
    return;
  }

  localStorage.setItem(STATSIG_STALE_CACHE_CLEARED_AT_KEY, new Date().toISOString());
}

function getStatsigClientKey() {
  handleClearingStaleStatsigCacheForTargetedAppMigration();
  return TARGETED_APP_STATSIG_CLIENT_KEY;
}

const STATSIG_CLIENT_TIMEOUT_MS = secondsToMilliseconds(10);

export const Statsig = new StatsigClient(getStatsigClientKey(), {}, statsigOptions);
subscribeStatsigExposuresToDatadog(Statsig);

export type StatsigProviderProps = {
  children: React.ReactNode;
};

/**
 * Callback function to be executed after Statsig client initialization is
 * successful.
 *
 * Useful for initializing non-React things that depend on Statsig values.
 */
function onStatsigInitializationSuccess() {
  ChromeToolbarPlaybackControlsSingleton.getInstance();
}

const STATSIG_CLIENT_RETRIES = 3;
const STATSIG_CLIENT_INITIALIZATION_DELAY_MS = 500;
const STATSIG_CLIENT_INITIALIZATION_BACKOFF_FACTOR = 2;

async function initializeStatsigClient() {
  const details = await Statsig.initializeAsync({
    timeoutMs: STATSIG_CLIENT_TIMEOUT_MS,
    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/API/fetch#priority
     */
    priority: 'high',
  });

  if (!details.success) {
    throw details.error ?? new Error('Unsuccessful Statsig client initialization');
  }

  return details;
}

const handleStatsigClientInitializationWithRetry = withRetry(initializeStatsigClient, {
  retries: STATSIG_CLIENT_RETRIES,
  initialDelayMs: STATSIG_CLIENT_INITIALIZATION_DELAY_MS,
  backoffFactor: STATSIG_CLIENT_INITIALIZATION_BACKOFF_FACTOR,
  onFailure: (args) => {
    logger.warn('Unsuccessful Statsig client initialization', args);
  },
});

async function handleStatsigClientInitialization() {
  try {
    await handleStatsigClientInitializationWithRetry();
    onStatsigInitializationSuccess();
  } catch (error) {
    logger.error('Initialize Statsig client error', {}, error as Error);
  }
}

/**
 * Statsig Provider for the application. Initializes the {@link Statsig} client.
 */
export function StatsigProvider({ children }: StatsigProviderProps) {
  const { loading } = useAsync(handleStatsigClientInitialization, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return <BaseStatsigProvider client={Statsig}>{children}</BaseStatsigProvider>;
}
